import { Component, ElementRef, Input, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { IDetect } from 'src/app/shared/directives/NgxBarCodePutDirective';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { mergeMap, of, Subscription, take } from 'rxjs';
import { Router } from '@angular/router';
import { ResourcesService } from 'src/app/shared/services/resources.service';
import { ClinicalService } from './shared/clinical.service';
import { ManualEntryReason, PatientTherapy, ResponseStatus, SearchEventType, SearchPage, ValidationType } from './shared/clinical.enum';
import { SearchRequest } from 'src/app/shared/models/search-request';
import { UserSessionService } from '../users/shared/user-session.service';
import { ICreateDevice } from 'src/app/shared/models/IDevice';
import { HttpErrorResponse } from '@angular/common/http';
import { PrefixSuffixService } from '../prefix-suffix/shared/prefix-suffix.service';
import { IPrefixSuffix } from 'src/app/shared/models/IPrefixSuffix';
import { IUserSession } from 'src/app/shared/models/IUserSession';
import { DeviceTypeService } from '../device-types/shared/device-type.service';
import { IDeviceType } from 'src/app/shared/models/IDevice-type';
import { ClinicialUtility } from './shared/clinical.util';

@Component({
  selector: 'app-clinical',
  templateUrl: './clinical.component.html',
  styleUrls: ['./clinical.component.css']
})
export class ClinicalComponent implements OnInit, OnDestroy {

  @ViewChild('clinicalcontent') validationModel: TemplateRef<ElementRef>;
  @ViewChild('manualEntryContent') manualEntryModel: TemplateRef<ElementRef>;
  @ViewChild('changeKeepAssociationContent') changeAssociationModel: TemplateRef<ElementRef>;
  @ViewChild('patientTherapyContent') patientTherapyModal: TemplateRef<ElementRef>;
  @ViewChild('deviceCreationModal') deviceCreationModal: TemplateRef<ElementRef>;

  @Input() modalBodyMessage: string;

  resourceKeyValue: any = null;
  modalRef: NgbModalRef;
  searchPatientDeviceValue: string = '';
  selectedManualEntryReason: string = '';
  isCommentsBoxEnabled: boolean = false;
  otherComments: string = '';
  manualEntryForm: FormGroup;
  patientTherapyForm: FormGroup;
  searchType: string = '';
  isLoading: boolean = false;
  isResetButtonEnabled: boolean = false;
  manualEntryReasonList = [];
  clinicalServiceSubscription!: Subscription;
  isFacilityConfigured: boolean;
  lblNotAuthorizedInstruction3: string = '';
  lblNotAuthorizedInstruction4: string = '';
  isManualOkButtonDisabled: boolean = true;
  clinicalSearchPlaceholder: string = '';
  searchAPIResponse: SearchRequest;
  patientTherapy: typeof PatientTherapy = PatientTherapy;
  modelsRegex = [];
  prefixSuffixData: IPrefixSuffix
  ngOnInit(): void {
    this.isLoading = true;
    localStorage.removeItem("SearchPage");
    this.userSessionService.getUserSession().pipe(
      take(1),
      mergeMap((session: IUserSession) => {
        this.isLoading = false;
        this.isFacilityConfigured = session?.facilityId ? true : false;
        if (session?.facilityId) {
          return this.prefixSuffixService.getPrefixSuffixByFacility(session?.facilityId);
        } else {
          return of(undefined);
        }
      }),
      mergeMap((prefixSuffix: IPrefixSuffix) => {
        if (prefixSuffix) {
          this.prefixSuffixData = prefixSuffix;
          return this.deviceTypeService.getAllDeviceTypesWithModels();
        } else {
          return of(undefined);
        }
      })).subscribe((deviceTypes: IDeviceType[]) => {
        if (deviceTypes && deviceTypes.length > 0) {
          this.modelsRegex = deviceTypes.map(x => x.regex);
        }
      })

    this.resourcesService.bsObject.subscribe(result => {
      result && (this.resourceKeyValue = result);
      this.setControlValues();
      this.setmanualEntryResonList();
    });
  }

  constructor(private modalService: NgbModal,
    private userSessionService: UserSessionService,
    private fb: FormBuilder,
    private router: Router,
    private clinicalService: ClinicalService,
    public prefixSuffixService: PrefixSuffixService,
    private deviceTypeService: DeviceTypeService,
    private resourcesService: ResourcesService) {
    this.manualEntryForm = this.fb.group({
      'manualEntryReason': ['', Validators.required],
      'othercomments': [''],
    });
    this.patientTherapyForm = this.fb.group({
      'patientTherapyName': ['', Validators.required],
    });
  }

  // Static list of manual entry reasons 
  setmanualEntryResonList() {
    this.manualEntryReasonList = [
      {
        label: this.resourceKeyValue.clinicianBarcodeDamaged,
        value: '1'
      },
      {
        label: this.resourceKeyValue.clinicianBarcodeNotAvailable,
        value: '2'
      },
      {
        label: this.resourceKeyValue.clinicianBarcodeWouldNotScan,
        value: '3'
      },
      {
        label: this.resourceKeyValue.clinicianOther,
        value: '4'
      },
    ];
  }

  get manualFormData(): any { return this.manualEntryForm.controls; }
  get patientTherapyFormData(): any { return this.patientTherapyForm.controls; }

  submitManualEntryForm() {
    this.otherComments = this.manualFormData.othercomments.value;
    if (this.manualFormData.manualEntryReason.value == ManualEntryReason.Other &&
      (this.otherComments == "" || this.otherComments == null)) {
      return false;
    }
    else {
      this.isLoading = true;
      this.searchPatientOrDeviceInfo(this.createSearchRequest());
      this.closeManualModal();
    }
  }

  resetSearchText() {
    this.searchPatientDeviceValue = '';
    this.isResetButtonEnabled = false;
  }

  changeManualReason(selectedEntry: any) {
    if (selectedEntry.value != ManualEntryReason.Other) {
      this.otherComments = '';
      this.isManualOkButtonDisabled = false;
    }
    this.isCommentsBoxEnabled = selectedEntry.value == ManualEntryReason.Other;
    this.selectedManualEntryReason = selectedEntry.label;
    this.isManualOkButtonDisabled = selectedEntry.value == ManualEntryReason.Other && this.otherComments == '';
  }

  // Scanner event on textbox input
  onDetected(event: IDetect) {
    if (event.type == SearchEventType.Scanner) {
      this.searchType = SearchEventType.Scanner;
      // call to search API
      this.isLoading = true;
      this.searchPatientOrDeviceInfo(this.createSearchRequest());
    }
    else if (event.type == SearchEventType.Keyboard) {
      this.toggleResetButton(event.value?.toString());
      if (event.event?.keyCode == 13) {
        this.searchType = SearchEventType.Manual;
        this.manualEntryForm.reset();
        this.isCommentsBoxEnabled = false;
        this.openModal(this.manualEntryModel);
      }
    }
  }

  onDelete(event: IDetect) {
    this.toggleResetButton(event.value?.toString());
  }

  toggleResetButton(value: string) {
    this.isResetButtonEnabled = value == '' ? false : true;
  }

  onKeypressEvent(event: any) {
    if (event.keyCode == 13 && event.target.value.trim() == '') {
      this.modalBodyMessage = this.resourceKeyValue.clinicianModalBodyMessage;
      this.openModal(this.validationModel);
    }
  }

  onCommentsKeyup(value: any) {
    this.isManualOkButtonDisabled = value == '';
  }

  // handle search icon click
  searchPatientDevice() {
    let searchedValue = this.searchPatientDeviceValue.trim();
    if (searchedValue == undefined || searchedValue == "") {
      this.modalBodyMessage = this.resourceKeyValue.clinicianModalBodyMessage;
      this.openModal(this.validationModel);
    }
    else {
      this.manualEntryForm.reset();
      this.openModal(this.manualEntryModel);
    }
  }

  closeManualModal(): void {
    this.closeModal();
    this.searchPatientDeviceValue = '';
    this.otherComments = '';
    this.selectedManualEntryReason = '';
    this.toggleResetButton(this.searchPatientDeviceValue);
    this.isManualOkButtonDisabled = true;
  }

  closeModal(): void {
    this.modalRef.close();
  }

  openModal(model: TemplateRef<ElementRef>) {
    this.modalRef = this.modalService.open(model, { backdrop: 'static', keyboard: false });
  }

  createSearchRequest(): SearchRequest {
    const searchPatientDeviceValueList = ClinicialUtility.createPatientDeviceSearch(this.searchPatientDeviceValue.trim(), this.prefixSuffixData, this.modelsRegex);
    this.searchPatientDeviceValue = searchPatientDeviceValueList[0];
    let searchRequest = {
      searchText: this.searchPatientDeviceValue.trim(),
      searchType: this.searchType,
      manualReason: this.selectedManualEntryReason,
      otherComments: this.otherComments,
      facilityId: Number(localStorage.getItem('facilityId')),
      searchDelimeterValues: searchPatientDeviceValueList
    };
    return searchRequest;
  }

  searchPatientOrDeviceInfo(searchRequest: SearchRequest): any {

    this.clinicalServiceSubscription = this.clinicalService.searchPatientOrDeviceId(searchRequest).subscribe(response => {
      this.isLoading = false;
      if (response.status == ResponseStatus.Error) {
        this.searchAPIResponse = response;
        this.searchAPIResponse.searchText = searchRequest.searchText;;
        // Device regex matched but device does not exists
        if (response.validationType == ValidationType.DeviceRegexMatchedButInvalidDevice) {
          this.openCreateDeviceModal(this.resourceKeyValue.deviceCreationConfirmation);
        }
        // Device regex matched but device exists in different facility
        else if (response.validationType == ValidationType.RegexMatchDeviceInDifferentFacility) {
          this.openCreateDeviceModal(this.resourceKeyValue.differentFacilityCreateDevice);
        }
        // Invalid patient       
        else if (response.validationType == ValidationType.InvalidPatient) {
          this.modalBodyMessage = this.resourceKeyValue.invalidPatientId.replace('{patientId}', searchRequest.searchDelimeterValues.join(', '));
          this.openModal(this.validationModel);
        }
        else if (response.validationType == ValidationType.RegexMatchWithMultipleDeviceTypes) {
          this.modalBodyMessage = this.resourceKeyValue.deviceMatchWithMultipleTypes;
          this.openModal(this.validationModel);
        }
      }
      else if (response.status == ResponseStatus.Success) {
        if (response.validationType == ValidationType.ValidDevice) {
          localStorage.setItem("SearchPage", SearchPage.Device);
          this.searchAPIResponse = response;
          if (response.activeDeviceAssociation != null) {
            this.openChangeAssociationModal(response);
          }
          else {
            this.openPatientTherapyModal(this.searchAPIResponse);
          }
        }
        else {
          localStorage.setItem("SearchPage", SearchPage.Patient);
          this.redirectToDetailPage(response);
        }
      }
    },
      (error) => {
        this.isLoading = false;

      });
  }

  openChangeAssociationModal(response: SearchRequest) {
    let message = this.resourceKeyValue.changeAssociationBody.replace('{deviceTypeName}', response.deviceCatalog[0].deviceTypeName);
    this.modalBodyMessage = message.replace('{deviceIdentifier}', response.deviceCatalog[0].deviceIdentifier);
    this.setActiveAssociationData(response);
    this.openModal(this.changeAssociationModel);
  }

  setActiveAssociationData(response: SearchRequest) {
    let device = this.searchAPIResponse.deviceCatalog[0];
    device.associationId = this.searchAPIResponse.activeDeviceAssociation?.associationId;
    device.oldPatientId = this.searchAPIResponse.activeDeviceAssociation?.oldPatientId;
    device.oldPatientRecordId = this.searchAPIResponse.activeDeviceAssociation?.oldPatientRecordId;
    this.searchAPIResponse.deviceCatalog[0] = device;
  }

  changeAssociation() {
    this.closeModal();
    // patient therapy workflow for Arctic sun device
    this.openPatientTherapyModal(this.searchAPIResponse);
  }

  openPatientTherapyModal(searchAPIResponse: SearchRequest) {
    if (this.isArcticSunDeviceType(searchAPIResponse)) {
      this.patientTherapyForm.reset();
      this.openModal(this.patientTherapyModal);
    }
    else {
      this.redirectToDetailPage(searchAPIResponse);
    }
  }

  redirectToDetailPage(response: SearchRequest) {
    response.prefixSuffix = this.prefixSuffixData;
    response.deviceModelRegex = this.modelsRegex;
    this.clinicalService.sendPatientDeviceDetailData(response);
    this.router.navigate(['/clinician/patient-device-detail']);
  }

  isArcticSunDeviceType(response: SearchRequest) {
    return response.isArcticSunDevice != null && response.isArcticSunDevice;
  }

  submitPatientTherapyForm() {
    let therapyValue = this.patientTherapyFormData.patientTherapyName.value;
    this.searchAPIResponse.deviceCatalog[0].isNewPatient = therapyValue == PatientTherapy.NewTherapySession;
    this.closeModal();
    this.redirectToDetailPage(this.searchAPIResponse)
  }

  setControlValues(): void {
    this.lblNotAuthorizedInstruction3 = this.resourceKeyValue.lblNotAuthorizedInstruction3;
    this.lblNotAuthorizedInstruction4 = this.resourceKeyValue.lblNotAuthorizedInstruction4;
  }

  ngOnDestroy(): void {
    if (this.clinicalServiceSubscription && !this.clinicalServiceSubscription.closed) {
      this.clinicalServiceSubscription.unsubscribe();
    }
  }

  openCreateDeviceModal(modalBodyMessage: string) {
    this.modalBodyMessage = modalBodyMessage;
    this.openModal(this.deviceCreationModal);
  }

  createDevice() {
    this.closeModal();
    this.isLoading = true;
    this.clinicalService.createClinicianDevice(this.createRequestObject()).subscribe(response => {
      this.isLoading = false;
      if (response && response.status == ResponseStatus.Success) {
        this.searchAPIResponse = response;
        // device is created successfully so set that device page
        localStorage.setItem("SearchPage", SearchPage.Device);
        this.openPatientTherapyModal(this.searchAPIResponse);
      } else {
        this.modalBodyMessage = this.resourceKeyValue.deviceErrorMessageOnCreate;
        this.openModal(this.validationModel);
      }
    }, (error: HttpErrorResponse) => {
      this.isLoading = false;
      console.error(error);
      this.modalBodyMessage = this.resourceKeyValue.deviceErrorMessageOnCreate;
      this.openModal(this.validationModel);
    });
  }

  createRequestObject(): ICreateDevice {
    let requestObj = {
      id: -1,
      facilityID: Number(localStorage.getItem('facilityId')),
      deviceID: this.searchAPIResponse.searchText.trim(),
      deviceTypeID: this.searchAPIResponse.deviceModel.deviceTypeRecordId,
      deviceModelID: this.searchAPIResponse.deviceModel.deviceModelId,
      deviceAtlasKey: "",
      deviceTypeCode: this.searchAPIResponse.deviceModel.deviceTypeCode,
      type: this.searchAPIResponse.validationType
    };
    return requestObj;
  }

}
